import React, { useState, useEffect, useRef } from 'react';
import MainLayout from '../../../components/layout/MainLayout';
import { useAuth } from '../../../context/AuthContext';
import { WhatsAppSocketProvider, useWhatsAppSocket } from '../../../context/WhatsAppSocketContext';
import {
  Smartphone, QrCode, MessageSquare, Send, RefreshCw, PowerOff, ShieldAlert,
  User as UserIcon, Check, CheckCheck, Paperclip
} from 'lucide-react';
import { MediaSendButton, MediaPreview, LibrarySendButton } from '../../../components/whatsapp/MediaSendButton';
import QuickReplies from '../../../components/whatsapp/QuickReplies';
import MediaPickerModal from '../../../components/shared/MediaPickerModal';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const WhatsAppWebAdminContent = () => {
  const { user } = useAuth();
  const { socket, isConnected: socketConnected } = useWhatsAppSocket();

  const [activeTab, setActiveTab] = useState('connection'); // 'connection', 'chats'
  const [sessionStatus, setSessionStatus] = useState('not_initialized'); // 'not_initialized', 'initializing', 'qr_ready', 'connected', 'disconnected'
  const [qrCode, setQrCode] = useState('');
  const [sessionInfo, setSessionInfo] = useState(null);
  
  const [contacts, setContacts] = useState([]);
  const [activeContact, setActiveContact] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessageText, setNewMessageText] = useState('');
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [isFetchingStatus, setIsFetchingStatus] = useState(true);
  const messagesEndRef = useRef(null);

  // Auto Scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Fetch Session Status
  const fetchSessionStatus = async () => {
    try {
      setIsFetchingStatus(true);
      const token = localStorage.getItem('crm_token');
      const res = await fetch(`${API_BASE}/whatsapp-web/session/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSessionStatus(data.status || 'not_initialized');
        if (data.status === 'connected') {
          setSessionInfo({ phone: data.phoneNumber, name: data.displayName });
        }
        if (data.qrCode) {
          setQrCode(data.qrCode);
        }
      }
    } catch (err) {
      console.error('Error fetching WA Web session status:', err);
    } finally {
      setIsFetchingStatus(false);
    }
  };

  // Start Session
  const startSession = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('crm_token');
      const res = await fetch(`${API_BASE}/whatsapp-web/session/start`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setSessionStatus('initializing');
      }
    } catch (err) {
      console.error('Error starting WA Web session:', err);
    } finally {
      setLoading(false);
    }
  };

  // Logout Session
  const logoutSession = async () => {
    if (!window.confirm('Are you sure you want to log out from WhatsApp Web?')) return;
    try {
      setLoading(true);
      const token = localStorage.getItem('crm_token');
      await fetch(`${API_BASE}/whatsapp-web/session/logout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessionStatus('not_initialized');
      setQrCode('');
      setSessionInfo(null);
    } catch (err) {
      console.error('Error logging out WA Web session:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch Contacts
  const fetchContacts = async () => {
    try {
      const token = localStorage.getItem('crm_token');
      const res = await fetch(`${API_BASE}/whatsapp-web/contacts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setContacts(data);
      }
    } catch (err) {
      console.error('Error fetching WA Web contacts:', err);
    }
  };

  // Fetch Messages
  const fetchMessages = async (phone) => {
    try {
      const token = localStorage.getItem('crm_token');
      const res = await fetch(`${API_BASE}/whatsapp-web/messages/${phone}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(data);
        setContacts(prev => prev.map(c => c.phone === phone ? { ...c, unreadCount: 0 } : c));
      }
    } catch (err) {
      console.error('Error fetching WA Web messages:', err);
    }
  };

  // Send Message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if ((!newMessageText.trim() && !selectedMedia) || !activeContact || sendingMessage) return;

    try {
      setSendingMessage(true);
      const token = localStorage.getItem('crm_token');
      let res;

      if (selectedMedia) {
        const formData = new FormData();
        formData.append('to', activeContact.phone);
        if (newMessageText.trim()) formData.append('caption', newMessageText.trim());
        
        if (selectedMedia.fileUrl) {
          formData.append('mediaUrl', selectedMedia.fileUrl);
        } else {
          formData.append('file', selectedMedia);
        }

        res = await fetch(`${API_BASE}/whatsapp-web/send-media`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`
          },
          body: formData
        });
      } else {
        res = await fetch(`${API_BASE}/whatsapp-web/send`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ to: activeContact.phone, message: newMessageText.trim() })
        });
      }
      
      if (res.ok) {
        setNewMessageText('');
        setSelectedMedia(null);
      } else {
        const data = await res.json();
        alert(data.message || 'Failed to send message.');
      }
    } catch (err) {
      console.error('Error sending message:', err);
    } finally {
      setSendingMessage(false);
    }
  };

  // Initial Load
  useEffect(() => {
    fetchSessionStatus();
    fetchContacts();
  }, []);

  // On Contact Change
  useEffect(() => {
    if (activeContact) {
      fetchMessages(activeContact.phone);
    }
  }, [activeContact]);

  // Socket Events
  useEffect(() => {
    if (!socket) return;

    const handleQr = ({ qr }) => {
      setQrCode(qr);
      setSessionStatus('qr_ready');
    };

    const handleReady = ({ phoneNumber, displayName }) => {
      setSessionStatus('connected');
      setSessionInfo({ phone: phoneNumber, name: displayName });
      setQrCode('');
    };

    const handleDisconnected = ({ reason }) => {
      setSessionStatus('disconnected');
    };

    const handleNewMessage = ({ contactPhone, message }) => {
      if (activeContact && activeContact.phone === contactPhone) {
        setMessages(prev => [...prev, message]);
      }
      fetchContacts();
    };

    socket.on('wa_web_qr', handleQr);
    socket.on('wa_web_ready', handleReady);
    socket.on('wa_web_disconnected', handleDisconnected);
    socket.on('wa_web_new_message', handleNewMessage);

    return () => {
      socket.off('wa_web_qr', handleQr);
      socket.off('wa_web_ready', handleReady);
      socket.off('wa_web_disconnected', handleDisconnected);
      socket.off('wa_web_new_message', handleNewMessage);
    };
  }, [socket, activeContact]);


  return (
    <MainLayout>
      <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto', height: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <h1 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Smartphone color="var(--color-brand-primary)" /> WhatsApp Web (Isolated)
            </h1>
            <p style={{ color: 'var(--color-text-secondary)', margin: '4px 0 0' }}>Connect your device by scanning the QR code.</p>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={() => setActiveTab('connection')}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: 'none',
                background: activeTab === 'connection' ? 'var(--color-brand-primary)' : '#e2e8f0',
                color: activeTab === 'connection' ? '#fff' : '#475569',
                cursor: 'pointer',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <QrCode size={18} /> Connection
            </button>
            <button
              onClick={() => setActiveTab('chats')}
              disabled={sessionStatus !== 'connected'}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: 'none',
                background: activeTab === 'chats' ? 'var(--color-brand-primary)' : '#e2e8f0',
                color: activeTab === 'chats' ? '#fff' : '#475569',
                cursor: sessionStatus === 'connected' ? 'pointer' : 'not-allowed',
                opacity: sessionStatus === 'connected' ? 1 : 0.6,
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <MessageSquare size={18} /> Chats
            </button>
          </div>
        </div>

        {activeTab === 'connection' && (
          <div style={{ background: '#fff', borderRadius: '12px', padding: '32px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
            
            <div style={{ 
              background: 'rgba(245, 158, 11, 0.1)', 
              borderLeft: '4px solid #f59e0b', 
              padding: '16px', 
              borderRadius: '0 8px 8px 0', 
              marginBottom: '32px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px'
            }}>
              <ShieldAlert color="#f59e0b" style={{ flexShrink: 0 }} />
              <div>
                <h4 style={{ margin: '0 0 4px', color: '#b45309', fontWeight: 'bold' }}>Stability Requirements</h4>
                <p style={{ margin: 0, color: '#92400e', fontSize: '14px' }}>
                  For optimal stability and to prevent unexpected logouts: 
                  <br/>1. Use a <strong>WhatsApp Business</strong> account if possible.
                  <br/>2. Keep your mobile phone connected to the internet at all times.
                  <br/>3. Do not log out from the mobile app. The session will automatically recover from server restarts.
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 0' }}>
              
              {isFetchingStatus ? (
                <div style={{ padding: '40px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <RefreshCw size={48} color="var(--color-brand-primary)" className="spin" style={{ marginBottom: '16px' }} />
                  <h3 style={{ margin: '0', color: '#334155' }}>Loading Session...</h3>
                </div>
              ) : sessionStatus === 'not_initialized' ? (
                <>
                  <QrCode size={64} color="#cbd5e1" style={{ marginBottom: '16px' }} />
                  <h3 style={{ margin: '0 0 16px', color: '#334155' }}>Session Not Started</h3>
                  <button 
                    onClick={startSession}
                    disabled={loading}
                    style={{
                      background: 'var(--color-brand-primary)',
                      color: 'white',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      fontWeight: 'bold',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      boxShadow: '0 4px 14px 0 rgba(0,0,0,0.1)'
                    }}
                  >
                    {loading ? <RefreshCw className="spin" size={18} /> : <PowerOff size={18} />}
                    Initialize Session
                  </button>
                </>
              ) : sessionStatus === 'initializing' ? (
                <>
                  <RefreshCw size={48} color="var(--color-brand-primary)" className="spin" style={{ marginBottom: '16px' }} />
                  <h3 style={{ margin: '0', color: '#334155' }}>Generating QR Code...</h3>
                  <p style={{ color: '#64748b' }}>Please wait, this might take a few seconds.</p>
                </>
              ) : sessionStatus === 'qr_ready' ? (
                <>
                  {qrCode ? (
                    <div style={{ padding: '16px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '16px', marginBottom: '16px', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' }}>
                      <img src={`https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(qrCode)}`} alt="WhatsApp QR Code" width="250" height="250" />
                    </div>
                  ) : (
                    <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <RefreshCw size={40} color="var(--color-brand-primary)" className="spin" style={{ marginBottom: '16px' }} />
                      <p style={{ color: '#64748b', textAlign: 'center', margin: 0 }}>Waiting for fresh QR code...<br/>(This can take up to 20 seconds)</p>
                    </div>
                  )}
                  <h3 style={{ margin: '0 0 8px', color: '#334155' }}>Scan to Connect</h3>
                  <p style={{ color: '#64748b', textAlign: 'center', maxWidth: '400px' }}>
                    Open WhatsApp on your phone > Menu > Linked Devices > Link a Device.
                  </p>
                </>
              ) : sessionStatus === 'connected' ? (
                <>
                  <div style={{ background: '#dcfce7', padding: '24px', borderRadius: '50%', marginBottom: '16px' }}>
                    <CheckCheck size={48} color="#22c55e" />
                  </div>
                  <h3 style={{ margin: '0 0 8px', color: '#166534' }}>Successfully Connected</h3>
                  {sessionInfo && (
                    <p style={{ color: '#15803d', fontWeight: '500', marginBottom: '24px' }}>
                      {sessionInfo.name} ({sessionInfo.phone})
                    </p>
                  )}
                  <button 
                    onClick={logoutSession}
                    disabled={loading}
                    style={{
                      background: '#ef4444',
                      color: 'white',
                      border: 'none',
                      padding: '10px 20px',
                      borderRadius: '8px',
                      fontWeight: 'bold',
                      cursor: loading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Logout Device
                  </button>
                </>
              ) : sessionStatus === 'disconnected' ? (
                <>
                  <ShieldAlert size={48} color="#ef4444" style={{ marginBottom: '16px' }} />
                  <h3 style={{ margin: '0 0 16px', color: '#ef4444' }}>Session Disconnected</h3>
                  <button 
                    onClick={startSession}
                    disabled={loading}
                    style={{
                      background: 'var(--color-brand-primary)',
                      color: 'white',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      fontWeight: 'bold',
                      cursor: loading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Restart Session
                  </button>
                </>
              ) : null}

            </div>
          </div>
        )}

        {activeTab === 'chats' && sessionStatus === 'connected' && (
          <div style={{ display: 'flex', height: 'calc(100vh - 200px)', background: '#fff', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
            
            {/* Contacts List */}
            <div style={{ width: '320px', borderRight: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
              <div style={{ padding: '16px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>Recent Chats</h3>
              </div>
              <div style={{ flex: 1, overflowY: 'auto' }}>
                {contacts.length === 0 ? (
                  <p style={{ padding: '20px', textAlign: 'center', color: '#94a3b8' }}>No chats found.</p>
                ) : (
                  contacts.map(c => (
                    <div 
                      key={c.phone} 
                      onClick={() => setActiveContact(c)}
                      style={{ 
                        padding: '12px 16px', 
                        borderBottom: '1px solid #f1f5f9',
                        cursor: 'pointer',
                        background: activeContact?.phone === c.phone ? '#f1f5f9' : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        transition: 'background 0.2s'
                      }}
                    >
                      <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'var(--color-brand-primary)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>
                        {c.name ? c.name.charAt(0).toUpperCase() : <UserIcon size={20} />}
                      </div>
                      <div style={{ flex: 1, overflow: 'hidden' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                          <span style={{ fontWeight: '600', color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name || c.phone}</span>
                          <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                            {new Date(c.lastMessageAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '13px', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '180px' }}>
                            {c.lastMessagePreview}
                          </span>
                          {c.unreadCount > 0 && (
                            <span style={{ background: '#22c55e', color: 'white', fontSize: '10px', fontWeight: 'bold', padding: '2px 6px', borderRadius: '10px' }}>
                              {c.unreadCount}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Chat Area */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#eef2f6' }}>
              {activeContact ? (
                <>
                  {/* Chat Header */}
                  <div style={{ padding: '16px', background: 'white', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '12px' }}>
                     <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'var(--color-brand-primary)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>
                        {activeContact.name ? activeContact.name.charAt(0).toUpperCase() : <UserIcon size={20} />}
                      </div>
                      <div>
                        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>{activeContact.name || activeContact.phone}</h3>
                        <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>{activeContact.phone}</p>
                      </div>
                  </div>

                  {/* Messages List */}
                  <div style={{ flex: 1, overflowY: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {messages.map(msg => {
                      const isMe = msg.direction === 'outbound';
                      return (
                        <div key={msg._id} style={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start' }}>
                          <div style={{
                            maxWidth: '70%',
                            padding: '10px 14px',
                            borderRadius: '12px',
                            background: isMe ? 'var(--color-brand-primary)' : 'white',
                            color: isMe ? 'white' : '#1e293b',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                            position: 'relative'
                          }}>
                            {msg.type !== 'text' && (
                              <div style={{ fontSize: '11px', fontStyle: 'italic', marginBottom: '4px', opacity: 0.8 }}>
                                [{msg.type} message]
                              </div>
                            )}
                            {msg.mediaUrl && (
                              ['image', 'video'].includes(msg.type) ? (
                                <img 
                                  src={msg.mediaUrl.startsWith('http') ? msg.mediaUrl : `http://localhost:5000${msg.mediaUrl}`} 
                                  alt="Media attachment" 
                                  style={{ maxWidth: '100%', borderRadius: '8px', marginBottom: '4px' }} 
                                  onError={(e) => {
                                    e.target.src = msg.mediaUrl.startsWith('http') ? msg.mediaUrl : `${API_BASE.replace('/api', '')}${msg.mediaUrl}`;
                                  }}
                                />
                              ) : (
                                <a 
                                  href={msg.mediaUrl.startsWith('http') ? msg.mediaUrl : `${API_BASE.replace('/api', '')}${msg.mediaUrl}`} 
                                  target="_blank" rel="noreferrer"
                                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px', background: 'rgba(0,0,0,0.05)', borderRadius: '6px', textDecoration: 'none', color: isMe ? 'white' : '#0f172a', fontSize: '13px', fontWeight: 500, marginBottom: '4px' }}
                                >
                                  <Paperclip size={16} /> Document / Media
                                </a>
                              )
                            )}
                            {msg.body && (
                              <div style={{ fontSize: '14px', lineHeight: '1.4', wordBreak: 'break-word' }}>
                                {msg.body}
                              </div>
                            )}
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px', marginTop: '4px', fontSize: '10px', color: isMe ? 'rgba(255,255,255,0.7)' : '#94a3b8' }}>
                              {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Input Area */}
                  <MediaPreview selectedMedia={selectedMedia} onClear={() => setSelectedMedia(null)} />
                  <div style={{ padding: '16px', background: 'white', borderTop: '1px solid #e2e8f0' }}>
                    <form onSubmit={handleSendMessage} style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                      <QuickReplies onSelect={(reply) => setNewMessageText(prev => prev ? prev + ' ' + reply : reply)} />
                      <MediaSendButton onSelect={(file) => setSelectedMedia(file)} />
                      <LibrarySendButton onClick={() => setShowMediaPicker(true)} />
                      <input 
                        type="text" 
                        value={newMessageText}
                        onChange={e => setNewMessageText(e.target.value)}
                        placeholder="Type a message..."
                        disabled={sendingMessage}
                        style={{
                          flex: 1,
                          padding: '12px 16px',
                          borderRadius: '24px',
                          border: '1px solid #cbd5e1',
                          outline: 'none',
                          fontSize: '14px'
                        }}
                      />
                      <button 
                        type="submit"
                        disabled={(!newMessageText.trim() && !selectedMedia) || sendingMessage}
                        style={{
                          width: '44px',
                          height: '44px',
                          borderRadius: '50%',
                          background: 'var(--color-brand-primary)',
                          color: 'white',
                          border: 'none',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: ((newMessageText.trim() || selectedMedia) && !sendingMessage) ? 'pointer' : 'not-allowed',
                          opacity: ((newMessageText.trim() || selectedMedia) && !sendingMessage) ? 1 : 0.5
                        }}
                      >
                        <Send size={18} style={{ marginLeft: '4px' }} />
                      </button>
                    </form>
                  </div>
                </>
              ) : (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
                  <MessageSquare size={64} style={{ marginBottom: '16px', opacity: 0.2 }} />
                  <p>Select a chat to start messaging</p>
                </div>
              )}
            </div>

          </div>
        )}

      </div>
      
      {showMediaPicker && (
        <MediaPickerModal
          isOpen={true}
          onClose={() => setShowMediaPicker(false)}
          onSelect={(file) => {
            setSelectedMedia(file);
            setShowMediaPicker(false);
          }}
        />
      )}

      <style>{`
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { 100% { transform: rotate(360deg); } }
      `}</style>
    </MainLayout>
  );
};

const WhatsAppWebAdmin = () => {
  return (
    <WhatsAppSocketProvider>
      <WhatsAppWebAdminContent />
    </WhatsAppSocketProvider>
  );
};

export default WhatsAppWebAdmin;
